import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { formatAud, formatDateTime, formatPerUnit, formatUnits } from '../lib/format';
import { Empty, Spinner } from '../components/ui';

interface Purchase {
  id: string;
  reference: string;
  placedAt: string;
  totalCents: number;
  discountCents: number;
  storeName: string;
  items: {
    name: string;
    quantityUnits: number;
    unitPriceCents: number;
    packsApplied: { packUnits: number; packPriceCents: number; count: number }[];
    lineTotalCents: number;
    lineDiscountCents: number;
  }[];
}

export function Purchases() {
  const purchases = useQuery({
    queryKey: ['purchases'],
    queryFn: () => apiFetch<Purchase[]>('/api/orders'),
  });

  if (purchases.isPending) return <Spinner label="Fetching your receipts" />;
  if (!purchases.data?.length)
    return <Empty title="Nothing bought yet" body="Your receipts will appear here." />;

  return (
    <div>
      <h1 className="text-3xl text-ink-950">My purchases</h1>

      <div className="mt-7 space-y-4">
        {purchases.data.map((purchase, index) => (
          <article
            key={purchase.id}
            className="tag rise p-5"
            style={{ animationDelay: `${index * 30}ms` }}
          >
            <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <div>
                <h2 className="text-lg leading-tight text-ink-950">{purchase.storeName}</h2>
                <p className="text-[13px] text-ink-500">{formatDateTime(purchase.placedAt)}</p>
              </div>
              <div className="text-right">
                <p className="tabular text-xl leading-none text-ink-950">
                  {formatAud(purchase.totalCents)}
                </p>
                <p className="tabular text-[11px] text-ink-500">{purchase.reference}</p>
              </div>
            </header>

            <ul className="mt-4 space-y-1.5 border-t hairline pt-3">
              {purchase.items.map((item, itemIndex) => (
                <li key={itemIndex} className="flex flex-wrap items-baseline gap-x-2 text-[14px]">
                  <span className="text-ink-900">{item.name}</span>
                  <span className="tabular text-ink-500">
                    {formatUnits(item.quantityUnits)} at {formatPerUnit(item.unitPriceCents)}
                  </span>
                  {/*
                    Kept from the snapshot, so an old receipt still explains its
                    own price. Sales made before packs existed carry an empty
                    breakdown rather than an invented one — their totals are
                    real, but they were priced by a rate that no longer exists.
                  */}
                  {item.packsApplied.map((pack) => (
                    <span key={pack.packUnits} className="sign text-[9px] text-ochre-600">
                      {pack.count} × {pack.packUnits}-pack
                    </span>
                  ))}
                  <span className="tabular ml-auto text-ink-900">
                    {formatAud(item.lineTotalCents)}
                  </span>
                </li>
              ))}
            </ul>

            {purchase.discountCents > 0 && (
              <p className="sign mt-3 text-[10px] text-leaf-700">
                Deals saved you {formatAud(purchase.discountCents)}
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
